import React, { useState } from 'react';
import {
  useAppContext,
  useTranslator,
  useToast,
  callNativeMobileApp,
} from '@front-activerse/common-components';
import { Button, Modal, ModalBody, ModalFooter, Row, Col } from 'reactstrap';

interface ITrackerActivityComponentProps {
  id: number;
  activityName: string;
  activityTypeName: string;
  caloriesBurned: number;
  durationSeconds: number;
  imageUrl: string;
  loggedAt: string;
  activitySourceType: string;
  removeActivity: (activityId: number) => Promise<any>;
  date: string;
}

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const remainingSeconds = seconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const remainingSeconds2 = remainingSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds2.toString().padStart(2, '0')}`;
};

const formatTimeFromDate = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const TrackerActivityComponent: React.FC<ITrackerActivityComponentProps> = ({
  id,
  activityName,
  activityTypeName,
  caloriesBurned,
  durationSeconds,
  imageUrl,
  loggedAt,
  activitySourceType,
  removeActivity,
  date,
}) => {
  const [showAlertModal, setShowAlertModal] = useState(false);
  const toggleModal = () => {
    setShowAlertModal(!showAlertModal);
  };
  const { t } = useTranslator();
  const {
    images: {
      trashIcon,
      trackerLoggedActivityThumb,
      trackerSyncedActivityAppleThumb,
      trackerSyncedActivityGoogleThumb,
    },
  } = useAppContext();

  const { toastError } = useToast();

  const renderSourceType = () => {
    if (activitySourceType === 'video_workout') {
      return t('tracker.history.entryType.workout');
    } else if (activitySourceType === 'manual') {
      return t('tracker.history.entryType.manual');
    } else if (activitySourceType === 'apple_health') {
      return t('tracker.history.entryType.synced');
    } else if (activitySourceType === 'google_fit') {
      return t('tracker.history.entryType.synced');
    } else {
      return t('tracker.history.entryType.workout');
    }
  };

  const removeActivityHandler = async () => {
    setShowAlertModal(false);
    const response = await removeActivity(id);
    if (response.code === 200) {
      // refresh native tracker data
      callNativeMobileApp({
        action: 'refresh_data',
        payload: {
          module: 'tracker',
          date: date,
        },
      });
    } else {
      toastError();
    }
  };

  const renderImage = () => {
    let image = null;

    if (imageUrl) {
      image = imageUrl;
    } else {
      if (activitySourceType === 'video_workout') {
        image = trackerLoggedActivityThumb;
      } else if (activitySourceType === 'apple_health') {
        image = trackerSyncedActivityAppleThumb;
      } else if (activitySourceType === 'google_fit') {
        image = trackerSyncedActivityGoogleThumb;
      } else {
        image = trackerLoggedActivityThumb;
      }
    }

    return image;
  };

  return (
    <>
      <div className="tracker-history-item d-flex">
        <img src={renderImage()} className="tracker-history-item-icon" alt="" />

        <div className="flex-grow-1 text-start">
          <div className="tracker-history-item-meta mb-xxs mt-s">
            <p className="description text-text-variant-color mb-0">
              <span>{formatTimeFromDate(loggedAt)}</span>
              <strong className="bullet-separator mx-1">•</strong>
              <span>{renderSourceType()}</span>
            </p>
          </div>
          <p className="small mb-xxs">{activityName ? activityName : activityTypeName}</p>
          <div className="d-flex justify-content-between">
            <p className="description text-text-main-color mb-s">
              <span role="img" aria-label="weightlifting">
                🏋️‍♀️
              </span>{' '}
              {caloriesBurned} kcal
            </p>
            <p className="description text-text-variant-color mb-s">
              {t('tracker.history.time.title')}{' '}
              <span className="text-text-main-color">{formatDuration(durationSeconds)}</span>
            </p>
          </div>
        </div>

        <div className="tracker-history-item-actions">
          <Button className="p-0" color="link" onClick={toggleModal}>
            <img src={trashIcon} alt="" />
          </Button>
        </div>
      </div>

      <Modal isOpen={showAlertModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold mb-l mt-s">
            {activitySourceType === 'video_workout' || activitySourceType === 'manual'
              ? t('tracker.history.removePopup.appWorkout.title')
              : t('tracker.history.removePopup.syncedActivity.title')}
          </h4>

          <p className="mb-0 text-text-variant-color">
            {activitySourceType === 'video_workout'
              ? t('tracker.history.removePopup.appWorkout.description')
              : t('tracker.history.removePopup.syncedActivity.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Row className="w-100 m-0">
            <Col xs="6" className="ps-0 pe-2">
              <Button color="secondary" outline className="w-100" onClick={() => toggleModal()}>
                {t('tracker.history.removePopup.cancelButton')}
              </Button>
            </Col>
            <Col xs="6" className="ps-2 pe-0">
              <Button
                color="primary"
                className="w-100"
                onClick={() => removeActivityHandler()}
                disabled={!showAlertModal}
              >
                {t('tracker.history.removePopup.removeButton')}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TrackerActivityComponent;
